/**
 * Poly Hydration — MutationObserver Utilities
 *
 * Pure utility functions for attaching a MutationObserver to block containers
 * and summarizing the resulting mutation records into quantitative counts.
 */

import type { MutationSummary, ObserverHandle } from './types';
import { log } from './logger';

/**
 * Categorizes an array of MutationRecords into a quantitative summary.
 *
 * Records are bucketed by type: childList mutations are counted separately from
 * attribute mutations, which are further split into class, style, and other.
 * This is a pure function with no side effects — safe to call from tests.
 */
export function summarizeMutations(records: MutationRecord[]): MutationSummary {
  let classChanges = 0;
  let styleChanges = 0;
  let otherAttributeChanges = 0;
  let childListChanges = 0;

  for (const record of records) {
    if (record.type === 'childList') {
      childListChanges++;
    } else if (record.attributeName === 'class') {
      classChanges++;
    } else if (record.attributeName === 'style') {
      styleChanges++;
    } else {
      otherAttributeChanges++;
    }
  }

  return {
    classChanges,
    styleChanges,
    otherAttributeChanges,
    childListChanges,
    totalMutations: classChanges + styleChanges + otherAttributeChanges + childListChanges,
  };
}

/**
 * Attaches a MutationObserver to a block container for mismatch detection.
 *
 * The observer is configured with `attributes`, `childList`, and `subtree` so it
 * captures both attribute patches (class/style changes) and structural replacements
 * (where hydrate could not reconcile the server DOM and replaced subtrees).
 *
 * Returns `null` if the observer fails to attach (e.g. unsupported environment),
 * allowing hydration to proceed without mismatch tracking.
 */
export function attachObserver(block: Element): ObserverHandle | null {
  try {
    const observer = new MutationObserver(() => {});
    observer.observe(block, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return {
      takeRecords: () => observer.takeRecords(),
      disconnect: () => {
        const remaining = observer.takeRecords();
        observer.disconnect();
        return remaining;
      },
    };
  } catch (error) {
    log('error', 'Failed to attach MutationObserver:', error);
    return null;
  }
}
